import { useCallback, useMemo, useState } from 'react';
import {
  EMPTY_TIKTOK_SETTINGS,
  isTikTokPrivacyLevelDisabled,
  TikTokCreatorBlock,
  TikTokCreatorInfoAnswer,
  TikTokPostSettings,
  TikTokPrivacyLevel,
  tiktokDraftFromSettings,
  tiktokSettingsFromDraft,
  tiktokSettingsIssues,
  TikTokSettingsDraft,
  TikTokSettingsIssue,
} from 'fym-shared/src/music24/tiktokPostSettings';
import { useGetTikTokCreatorInfoQuery } from '../../../../../redux/api/fymAPI';

interface UseTikTokSettingsProps {
  accountId: number;
  /** True while TikTok is selected and its channel posts through the API. */
  isActive: boolean;
  /** The attached file, when there is one. */
  mediaFileId?: number;
  /** The length of the attached file in seconds, when the Composer knows it. */
  mediaDurationSec?: number;
}

export interface TikTokSettingsState {
  draft: TikTokSettingsDraft;
  /** The creator read from TikTok now. Null while it loads or when TikTok answered an error. */
  answer: TikTokCreatorInfoAnswer | undefined;
  isLoading: boolean;
  blockedReason: TikTokCreatorBlock | null;
  issues: TikTokSettingsIssue[];
  /** The settings to send, or null while the privacy level is not picked. */
  settings: TikTokPostSettings | null;
  /** True when nothing stops the post: every issue is gone and the creator can post. */
  isReady: boolean;
  setPrivacy: (level: TikTokPrivacyLevel) => void;
  setAllowComment: (value: boolean) => void;
  setAllowDuet: (value: boolean) => void;
  setAllowStitch: (value: boolean) => void;
  setDiscloseContent: (value: boolean) => void;
  setYourBrand: (value: boolean) => void;
  setBrandedContent: (value: boolean) => void;
  /** Puts the stored settings of a post into the form (an edit of a post that has them). */
  load: (stored: TikTokPostSettings | null) => void;
  reload: () => void;
}

/**
 * The state of the TikTok settings section: what the user chose, what TikTok says about the
 * creator right now, and the issues that stop the post (fym-shared `tiktokPostSettings.ts`).
 *
 * Nothing is picked for the user: the privacy level starts empty and every switch starts off. The
 * creator is read each time the section opens (TikTok Direct Post G1, G2).
 */
export function useTikTokSettings({
  accountId,
  isActive,
  mediaFileId,
  mediaDurationSec,
}: UseTikTokSettingsProps): TikTokSettingsState {
  const [draft, setDraft] = useState<TikTokSettingsDraft>(() => ({ ...EMPTY_TIKTOK_SETTINGS }));
  const query = useGetTikTokCreatorInfoQuery(
    { accountId, mediaFileId },
    { skip: !isActive, refetchOnMountOrArgChange: true }
  );
  const answer = query.data;
  const creator = answer?.creator ?? null;

  const patch = useCallback((change: Partial<TikTokSettingsDraft>) => {
    setDraft((current) => ({ ...current, ...change }));
  }, []);

  /** A change of the disclosure can disable the level that was picked: it is then picked again. */
  const patchAndKeepLevel = useCallback((change: Partial<TikTokSettingsDraft>) => {
    setDraft((current) => {
      const next = { ...current, ...change };
      if (next.privacyLevel !== null && isTikTokPrivacyLevelDisabled(next.privacyLevel, next)) {
        return { ...next, privacyLevel: null };
      }
      return next;
    });
  }, []);

  const setPrivacy = useCallback((level: TikTokPrivacyLevel) => patch({ privacyLevel: level }), [patch]);
  const setAllowComment = useCallback((value: boolean) => patch({ allowComment: value }), [patch]);
  const setAllowDuet = useCallback((value: boolean) => patch({ allowDuet: value }), [patch]);
  const setAllowStitch = useCallback((value: boolean) => patch({ allowStitch: value }), [patch]);
  const setDiscloseContent = useCallback(
    (value: boolean) =>
      patchAndKeepLevel(
        value ? { discloseContent: true } : { discloseContent: false, yourBrand: false, brandedContent: false }
      ),
    [patchAndKeepLevel]
  );
  const setYourBrand = useCallback((value: boolean) => patchAndKeepLevel({ yourBrand: value }), [patchAndKeepLevel]);
  const setBrandedContent = useCallback(
    (value: boolean) => patchAndKeepLevel({ brandedContent: value }),
    [patchAndKeepLevel]
  );
  const load = useCallback((stored: TikTokPostSettings | null) => setDraft(tiktokDraftFromSettings(stored)), []);
  const { refetch } = query;
  const reload = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const durationSec = mediaDurationSec ?? answer?.media?.durationSec ?? null;
  const issues = useMemo<TikTokSettingsIssue[]>(
    () =>
      tiktokSettingsIssues(draft, {
        creator,
        canPost: answer?.canPost === true,
        mediaKind: mediaFileId === undefined ? null : 'video',
        durationSec,
        watermarked: answer?.media?.watermarked === true,
      }),
    [answer, creator, draft, durationSec, mediaFileId]
  );
  const settings = useMemo(() => tiktokSettingsFromDraft(draft), [draft]);
  const blockedReason = answer?.blockedReason ?? (query.isError ? 'unavailable' : null);

  return {
    draft,
    answer,
    isLoading: query.isFetching && answer === undefined,
    blockedReason,
    issues,
    settings,
    isReady: isActive && settings !== null && issues.length === 0,
    setPrivacy,
    setAllowComment,
    setAllowDuet,
    setAllowStitch,
    setDiscloseContent,
    setYourBrand,
    setBrandedContent,
    load,
    reload,
  };
}
